import { useState } from "react";
import { useTranslation } from "react-i18next";

interface UseEventModalReturn {
    showModal: boolean;
    showShareModal: boolean; 
    showExportModal: boolean; 
    modalData: {
        title: string;
        subtitle: string;
        subtitle2: string;
        countValues: number;
        bodyLabel: string;
        type: 'delete' | 'end' | 'info' | 'upload' | 'exportCsv';
    };
    error: string;
    endDate: Date | null;
    startDate: Date | null;
    isLoading: boolean;
    setLoading: (loading: boolean) => void;
    closeModal: () => void;
    setEndDate: (date: Date | null) => void;
    setStartDate: (date: Date | null) => void;
    setError: (error: string) => void;
    onEndDateChange?: (date: Date | null) => void;
    openInfoModal: () => void; // Added upload modal function
    openShareModal: () => void; 
    openExportModal: () => void; 
    setModalCount: (count: number) => void; // ADDED: New function to update the count
}

type ModalType = 'delete' | 'end' | 'info' | 'upload' | 'exportCsv';

interface ModalData {
    title: string;
    subtitle: string;
    subtitle2: string;
    countValues: number;
    bodyLabel: string;
    type: ModalType;
}

export function useGroupButtonsModal(): UseEventModalReturn {
    const [showModal, setShowModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);


    const [modalData, setModalData] = useState<ModalData>({
        title: "default",
        subtitle: "default",
        subtitle2: "default",
        countValues: 0,
        bodyLabel: "default",
        type: "delete"
    });

    const { t } = useTranslation();

    const createModalData = (overrides: Partial<ModalData> = {}): ModalData => ({
        title: "",
        subtitle: "",
        subtitle2: "",
        bodyLabel: "",
        type: "info",
        countValues: 0,
        ...overrides
    });

    const openModal = (
        setVisibility: (show: boolean) => void,
        modalConfig: ModalData
    ) => {
        setModalData(modalConfig);
        setVisibility(true);
    };

    const openInfoModal = () => {
        openModal(setShowModal, createModalData({
            title: t("genericDic:modal.PrintIndicatorModalTitle"),
            subtitle: t("genericDic:modal.PrintIndicatorModalSubtitle"),
            type: "info"
        }));
    };

    const openShareModal = () => {
        openModal(setShowShareModal, createModalData({
            title: t("genericDic:modal.PrintIndicatorModalTitle"),
            subtitle: t("genericDic:modal.PrintIndicatorModalSubtitle"),
            type: "info"
        }));
    };

    const openExportModal = () => {
        openModal(setShowExportModal, createModalData({
            title: t("dashboard:share.exportTitle"),
            type: "exportCsv"
        }));
    };
  
    const closeModal = () => {
        setIsLoading(false); // Reset loading state al cerrar
        setShowModal(false);
        setShowShareModal(false);
        setShowExportModal(false);
        setError('')
    };

    const setLoading = (loading: boolean) => {
        setIsLoading(loading);
    };

    const setModalCount = (count: number) => {
        setModalData(prevData => ({ ...prevData, countValues: count }));
    };

    return {
        showModal,
        showShareModal,
        showExportModal,
        modalData,
        error,
        endDate,
        startDate,
        openInfoModal,
        openShareModal,
        openExportModal,
        closeModal,
        setEndDate,
        setStartDate,
        setError,
        isLoading,
        setLoading,
        setModalCount
    };
}
